import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookUser, Loader2 } from "lucide-react";

import { adminApi, formatApiError } from "../../../context/AuthContext";
import RecipientToCustomerAction from "../shared/RecipientToCustomerAction";

/**
 * Opgeslagen ontvangers van één klant (adresboek), met de bewuste actie
 * "Opslaan als klant". De zending houdt altijd zijn eigen ontvanger-snapshot;
 * dit boek is uitsluitend een invulhulp voor volgende zendingen.
 */
export default function CustomerRecipientsPanel({ customerId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/customers/${customerId}/recipients`);
      setRows(r.data?.recipients || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-8 border-t border-gray-200 pt-6" data-testid="klant-recipients-panel">
      <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-1 flex items-center gap-2">
        <BookUser className="w-5 h-5 text-[#0055FF]" /> Opgeslagen ontvangers
      </h3>
      <p className="text-xs text-gray-600 mb-3">
        Eerder gebruikte ontvangers van deze klant. Bij een nieuwe zending zijn
        deze direct te kiezen. Een bestaande zending verandert niet mee als je
        hier iets aanpast.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laden…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-500 italic" data-testid="klant-recipients-empty">
          Nog geen opgeslagen ontvangers voor deze klant.
        </p>
      ) : (
        <ul className="border border-gray-200 divide-y divide-gray-100">
          {rows.map((r) => (
            <li key={r.id}
                className="px-3 py-2 flex flex-wrap items-center gap-2"
                data-testid={`klant-recipient-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-[#082c50] break-words">
                  {r.name}
                  {r.company_name && <span className="text-gray-500 font-normal"> · {r.company_name}</span>}
                </div>
                <div className="text-xs text-gray-600 break-words">
                  {[r.street, r.house_number, r.postal_code, r.city, r.country]
                    .filter(Boolean).join(" ") || "—"}
                  {r.usage_count ? ` · ${r.usage_count}x gebruikt` : ""}
                </div>
              </div>
              <RecipientToCustomerAction
                recipient={r}
                onConverted={load}
                testIdPrefix="klant-recipient-convert"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
